export type TaxonomyItem = {
  id: string
  name: string
}

export type TaxonomyApiItem = TaxonomyItem | string

export const normalizeTaxonomyItem = (
  item: TaxonomyApiItem
): TaxonomyItem | null => {
  if (typeof item === "string") {
    const name = item.trim()
    return name ? { id: name, name } : null
  }

  const id = item?.id ? String(item.id) : ""
  const name = item?.name ? String(item.name).trim() : ""
  if (!id || !name) return null
  return { id, name }
}

export const normalizeTaxonomyItems = (
  items: TaxonomyApiItem[] = []
): TaxonomyItem[] =>
  items
    .map((item) => normalizeTaxonomyItem(item))
    .filter((item): item is TaxonomyItem => item !== null)

export const taxonomyToSelectOptions = (items: TaxonomyApiItem[] = []) =>
  normalizeTaxonomyItems(items).map((item) => ({
    label: item.name,
    value: item.id,
  }))

export const taxonomyItemNames = (items: TaxonomyApiItem[] = []) =>
  normalizeTaxonomyItems(items).map((item) => item.name)

export const joinTaxonomyNames = (items: TaxonomyApiItem[] = []) =>
  taxonomyItemNames(items).join(", ")

export const mergeTaxonomyItems = (
  collections: TaxonomyApiItem[][]
): TaxonomyItem[] => {
  const merged = new Map<string, TaxonomyItem>()
  collections.forEach((collection) => {
    normalizeTaxonomyItems(collection).forEach((item) => {
      merged.set(item.id, item)
    })
  })
  return Array.from(merged.values())
}

export const findTaxonomySelectOption = (
  options: { label: string; value: string }[],
  id?: string | null,
  legacyName?: string | null
) => {
  if (id) {
    return options.find((option) => option.value === id) ?? null
  }

  if (legacyName) {
    return (
      options.find(
        (option) => option.label.toLowerCase() === legacyName.toLowerCase()
      ) ?? { label: legacyName, value: legacyName }
    )
  }

  return null
}

const looksLikeObjectId = (value: string) => /^[a-f0-9]{24}$/i.test(value)

export const resolveTaxonomyLabel = (
  items: TaxonomyApiItem[] = [],
  idOrName?: string | null,
) => {
  const resolved = findTaxonomySelectOption(
    taxonomyToSelectOptions(items),
    idOrName,
    idOrName,
  )?.label

  if (resolved) return resolved
  if (idOrName && looksLikeObjectId(idOrName)) return "Unknown topic"
  return idOrName ?? ""
}

export type OrphanedTaxonomyRefs = {
  subject?: string
  topic?: string
  chapter?: string
}

export const isSubjectOptionMissing = (
  options: { label: string; value: string }[],
  id?: string | null
) => {
  if (!id) return false
  return !options.some((option) => option.value === id)
}

export const isTaxonomyOptionMissing = (
  options: { label: string; value: string }[],
  id?: string | null,
  legacyName?: string | null
) => {
  if (!id && !legacyName?.trim()) return false

  if (id) {
    return !options.some((option) => option.value === id)
  }

  const normalized = legacyName!.trim().toLowerCase()
  return !options.some(
    (option) => option.label.toLowerCase() === normalized
  )
}

export const formatOrphanedTaxonomyLabel = (
  id?: string | null,
  legacyName?: string | null
) => {
  if (legacyName?.trim()) return legacyName.trim()
  return "Removed item"
}

export const getOrphanedTaxonomyRefs = (
  topicOptions: { label: string; value: string }[],
  chapterOptions: { label: string; value: string }[],
  refs: {
    subject_id?: string | null
    subject_title?: string | null
    topic_id?: string | null
    topic?: string | null
    chapter_id?: string | null
    chapter?: string | null
  },
  subjectOptions: { label: string; value: string }[] = []
): OrphanedTaxonomyRefs => {
  const orphaned: OrphanedTaxonomyRefs = {}

  if (isSubjectOptionMissing(subjectOptions, refs.subject_id)) {
    orphaned.subject = formatOrphanedTaxonomyLabel(
      refs.subject_id,
      refs.subject_title
    )
  }

  if (
    isTaxonomyOptionMissing(topicOptions, refs.topic_id, refs.topic)
  ) {
    orphaned.topic = formatOrphanedTaxonomyLabel(refs.topic_id, refs.topic)
  }

  if (
    isTaxonomyOptionMissing(chapterOptions, refs.chapter_id, refs.chapter)
  ) {
    orphaned.chapter = formatOrphanedTaxonomyLabel(
      refs.chapter_id,
      refs.chapter
    )
  }

  return orphaned
}

export const orphanedSubjectError = (label: string) =>
  `Subject "${label}" was removed. Select a new subject.`

export const orphanedTopicError = (label: string) =>
  `Topic "${label}" was removed from this subject. Select a new topic.`

export const orphanedChapterError = (label: string) =>
  `Chapter "${label}" was removed from this subject. Select a new chapter.`
